import React, { useCallback, useEffect, useRef, useState } from "react";
import { Command } from "cmdk";
import { MapPin, X } from "lucide-react";
import { getCities } from "../lib/api";

// Keyboard-navigable city picker. Cities (with hotel counts) are fetched once
// on mount and filtered client-side as the user types.
export default function CityCombobox({ value, onChange, placeholder = "Search a city", ariaLabel = "City", autoFocus = false }) {
  const [cities, setCities] = useState([]);
  const [status, setStatus] = useState("loading");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const mounted = useRef(true);

  const load = useCallback(() => {
    setStatus("loading");
    getCities()
      .then(list => { if (mounted.current) { setCities(list.filter(c => c.hotel_count > 0)); setStatus("ready"); } })
      .catch(() => { if (mounted.current) setStatus("error"); });
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; };
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = event => { if (!rootRef.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  const select = city => {
    onChange(city);
    setQuery("");
    setOpen(false);
    inputRef.current?.blur();
  };

  const clear = () => {
    onChange("");
    setQuery("");
    inputRef.current?.focus();
  };

  return (
    <div ref={rootRef} className="relative w-full">
      <Command label={ariaLabel} loop shouldFilter className="relative">
        <MapPin className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-hs-muted" aria-hidden="true" />
        <Command.Input
          ref={inputRef}
          aria-label={ariaLabel}
          autoFocus={autoFocus}
          value={open ? query : (value || "")}
          onValueChange={next => { setQuery(next); if (!open) setOpen(true); }}
          onFocus={() => { setQuery(""); setOpen(true); }}
          onClick={() => setOpen(true)}
          onKeyDown={event => {
            if (event.key === "Escape" && open) { event.stopPropagation(); setOpen(false); }
          }}
          placeholder={value && open ? value : placeholder}
          className="input-base min-h-[44px] pl-10 pr-10"
        />
        {value && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear city"
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-hs-muted hover:bg-hs-sage hover:text-hs-charcoal"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {open && (
          <Command.List
            // Keep focus in the input so clicking an option doesn't blur-close the list first.
            onMouseDown={event => event.preventDefault()}
            className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-xl border border-hs-border bg-white p-1 shadow-lg"
          >
            {status === "loading" && (
              <Command.Loading>
                <div className="space-y-1 p-1" aria-label="Loading cities">
                  {[0, 1, 2].map(i => <div key={i} className="h-10 animate-pulse rounded-lg bg-hs-sage" />)}
                </div>
              </Command.Loading>
            )}
            {status === "error" && (
              <div className="flex items-center justify-between gap-3 px-3 py-3 text-sm text-hs-slate">
                Couldn't load cities.
                <button type="button" onClick={load} className="font-semibold text-hs-amber hover:text-hs-amber-dk">Retry</button>
              </div>
            )}
            {status === "ready" && (
              <Command.Empty className="px-3 py-3 text-sm text-hs-muted">No cities match “{query}”.</Command.Empty>
            )}
            {cities.map(city => {
              const chosen = city.city === value;
              return (
                <Command.Item
                  key={city.city}
                  value={city.city}
                  onSelect={() => select(city.city)}
                  className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-hs-charcoal data-[selected=true]:bg-hs-sage ${chosen ? "!bg-hs-amber-lt !text-hs-amber" : ""}`}
                >
                  <span className="font-medium">{city.city}</span>
                  <span className={`ml-auto text-xs ${chosen ? "text-hs-amber" : "text-hs-muted"}`}>
                    {city.hotel_count} {city.hotel_count === 1 ? "hotel" : "hotels"}
                  </span>
                </Command.Item>
              );
            })}
          </Command.List>
        )}
      </Command>
    </div>
  );
}
